import React, { useState, useEffect } from 'react';
import { Plus, Undo2, Copy, Check } from 'lucide-react';
import { getApiUrl } from '../lib/utils';

export const ReferralsTab = ({ adminKey }) => {
  const [founders, setFounders] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newFounder, setNewFounder] = useState({ name: '', email: '', referralCode: '' });
  const [error, setError] = useState('');
  const [copiedCode, setCopiedCode] = useState('');

  const load = async () => {
    const API_URL = getApiUrl();
    const res = await fetch(`${API_URL}/api/admin/founders`, { headers: { 'X-Admin-Key': adminKey } });
    if (res.ok) setFounders(await res.json());
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const handleAddFounder = async (e) => {
    e.preventDefault();
    setError('');
    const API_URL = getApiUrl();
    const res = await fetch(`${API_URL}/api/admin/founders`, {
      method: 'POST',
      headers: { 'X-Admin-Key': adminKey, 'Content-Type': 'application/json' },
      body: JSON.stringify(newFounder)
    });
    if (!res.ok) {
      const data = await res.json();
      setError(data.detail || 'Failed to add founder');
      return;
    }
    setNewFounder({ name: '', email: '', referralCode: '' });
    setShowAddForm(false);
    load();
  };

  const handleCredit = async (founderId, referredEmail) => {
    const API_URL = getApiUrl();
    const res = await fetch(`${API_URL}/api/admin/founders/${founderId}/credit`, {
      method: 'POST',
      headers: { 'X-Admin-Key': adminKey, 'Content-Type': 'application/json' },
      body: JSON.stringify({ referredEmail })
    });
    if (!res.ok) {
      const data = await res.json();
      alert(data.detail || 'Failed to credit');
      return;
    }
    load();
  };

  const handleUndo = async (founderId, referredEmail) => {
    const API_URL = getApiUrl();
    await fetch(`${API_URL}/api/admin/founders/${founderId}/undo`, {
      method: 'POST',
      headers: { 'X-Admin-Key': adminKey, 'Content-Type': 'application/json' },
      body: JSON.stringify({ referredEmail })
    });
    load();
  };

  const copyLink = (code) => {
    navigator.clipboard.writeText(`https://petechtor.org/?ref=${code}`);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(''), 1500);
  };

  const statusColor = (status) => {
    if (status === 'Credited') return 'bg-emerald-400/10 border-emerald-400/30 text-emerald-300';
    if (status === 'Ready to credit') return 'bg-amber-400/10 border-amber-400/30 text-amber-300';
    return 'bg-slate-700/30 border-slate-600/40 text-slate-400';
  };

  return (
    <div data-testid="referrals-tab">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-slate-400">Founding Guardians and who they referred.</p>
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          data-testid="add-founder-toggle-btn"
          className="inline-flex items-center gap-2 rounded-full bg-amber-400 px-4 py-2 text-sm font-medium text-slate-900 hover:bg-amber-300 transition-colors"
        >
          <Plus className="w-4 h-4" /> Add Founder
        </button>
      </div>

      {showAddForm && (
        <form onSubmit={handleAddFounder} className="mb-6 rounded-xl bg-slate-900/60 border border-slate-700/50 p-4 grid sm:grid-cols-4 gap-3">
          <input required placeholder="Name" value={newFounder.name} onChange={(e) => setNewFounder({ ...newFounder, name: e.target.value })} data-testid="new-founder-name-input" className="px-3 py-2 rounded-lg bg-slate-800/50 border border-slate-700/50 text-sm text-white placeholder:text-slate-500" />
          <input required type="email" placeholder="Email" value={newFounder.email} onChange={(e) => setNewFounder({ ...newFounder, email: e.target.value })} data-testid="new-founder-email-input" className="px-3 py-2 rounded-lg bg-slate-800/50 border border-slate-700/50 text-sm text-white placeholder:text-slate-500" />
          <input required placeholder="Referral Code (e.g. MARIA)" value={newFounder.referralCode} onChange={(e) => setNewFounder({ ...newFounder, referralCode: e.target.value.toUpperCase() })} data-testid="new-founder-code-input" className="px-3 py-2 rounded-lg bg-slate-800/50 border border-slate-700/50 text-sm text-white placeholder:text-slate-500" />
          <button type="submit" data-testid="new-founder-submit-btn" className="rounded-lg bg-amber-400 px-3 py-2 text-sm font-medium text-slate-900 hover:bg-amber-300">Save</button>
          {error && <p className="col-span-4 text-xs text-rose-400" data-testid="new-founder-error">{error}</p>}
        </form>
      )}

      {founders.length === 0 ? (
        <p className="text-center text-slate-500 py-12">No founders added yet.</p>
      ) : founders.map((founder) => (
        <div key={founder.id} className="mb-6 rounded-xl bg-slate-900/40 border border-slate-700/50 overflow-hidden" data-testid={`founder-block-${founder.id}`}>
          <div className="p-4 border-b border-slate-700/50 flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="font-semibold text-white">{founder.name} <span className="text-xs text-slate-500">({founder.email})</span></div>
              <button onClick={() => copyLink(founder.referralCode)} data-testid={`copy-referral-link-${founder.id}`} className="inline-flex items-center gap-1.5 mt-1 text-xs text-cyan-400 hover:text-cyan-300">
                {copiedCode === founder.referralCode ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                petechtor.org/?ref={founder.referralCode}
              </button>
            </div>
            <div className="flex items-center gap-4 text-sm">
              <div><span className="text-slate-500">Points:</span> <span className="font-semibold text-amber-300">{founder.points}</span></div>
              <div><span className="text-slate-500">Slots:</span> <span className="font-semibold text-white">{founder.slotsUsed}/2</span></div>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-800/30">
                <tr>
                  <th className="px-4 py-2 text-left text-xs text-slate-400 uppercase">Referred</th>
                  <th className="px-4 py-2 text-left text-xs text-slate-400 uppercase">Waitlist</th>
                  <th className="px-4 py-2 text-left text-xs text-slate-400 uppercase">Room</th>
                  <th className="px-4 py-2 text-left text-xs text-slate-400 uppercase">Feedback</th>
                  <th className="px-4 py-2 text-left text-xs text-slate-400 uppercase">Status</th>
                  <th className="px-4 py-2 text-right text-xs text-slate-400 uppercase"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/50">
                {founder.referred.length === 0 ? (
                  <tr><td colSpan="6" className="px-4 py-6 text-center text-slate-500">No referrals yet.</td></tr>
                ) : founder.referred.map((r) => (
                  <tr key={r.email}>
                    <td className="px-4 py-3 text-white">{r.name || '—'} <span className="text-xs text-slate-500">{r.email}</span></td>
                    <td className="px-4 py-3">{r.waitlist ? '✓' : '—'}</td>
                    <td className="px-4 py-3">{r.room ? '✓' : '—'}</td>
                    <td className="px-4 py-3">{r.feedback ? '✓' : '—'}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex rounded-full border px-2 py-0.5 text-xs ${statusColor(r.status)}`}>{r.status}</span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      {r.status === 'Ready to credit' && founder.slotsUsed < 2 && (
                        <button onClick={() => handleCredit(founder.id, r.email)} data-testid={`credit-btn-${founder.id}-${r.email}`} className="rounded-full bg-amber-400 px-3 py-1 text-xs font-medium text-slate-900 hover:bg-amber-300">+100</button>
                      )}
                      {r.status === 'Credited' && (
                        <button onClick={() => handleUndo(founder.id, r.email)} data-testid={`undo-btn-${founder.id}-${r.email}`} className="inline-flex items-center gap-1 text-xs text-rose-400 hover:text-rose-300"><Undo2 className="w-3.5 h-3.5" /> Undo</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
};
